import React from 'react';
import { Button, ButtonGroup } from '@lendi-ui/button';
import Modal from '@lendi-ui/modal';
import { ModalSize } from '@lendi-ui/modal/dist/declarations/src/index.style';

import { useAnalytics } from '@funnels/providers';
import { TrackingProps } from '@funnels/types';
import { run } from '@funnels/utils';

import { UppercaseBody } from '../../_common/style';
import { Content } from '../style';

interface FooterButtonGroupProps {
  trackingEvent: string;
  actionPrimaryButtonLabel: string;
  isActionPrimaryButtonDisabled?: boolean;
  actionSecondaryButtonLabel: string;
  onAction?: () => void;
  onSecondaryAction?: () => void;
  hideSecondaryButton?: boolean;
  hidePrimaryButton?: boolean;
  trackingProps?: TrackingProps;
}

function FooterButtonGroup(props: FooterButtonGroupProps) {
  const { tracking } = useAnalytics();
  const {
    hidePrimaryButton,
    hideSecondaryButton,
    actionSecondaryButtonLabel,
    actionPrimaryButtonLabel,
    trackingProps = {},
    isActionPrimaryButtonDisabled,
  } = props;

  if (!hidePrimaryButton && !hideSecondaryButton) {
    return (
      <ButtonGroup size="md" isFullWidth>
        <Button
          variant="primary"
          data-component="modal-button-action"
          onClick={run(props.onAction, () =>
            tracking?.button.primary({ text: actionPrimaryButtonLabel.toUpperCase(), ...trackingProps })
          )}
          isDisabled={isActionPrimaryButtonDisabled}
          isFullWidth
        >
          <UppercaseBody>{actionPrimaryButtonLabel}</UppercaseBody>
        </Button>
        <Button
          data-component="modal-secondary-button-action"
          variant="secondary"
          onClick={run(props.onSecondaryAction, () =>
            tracking?.button.secondary({ text: actionSecondaryButtonLabel.toUpperCase(), ...trackingProps })
          )}
          isFullWidth
        >
          <UppercaseBody>{actionSecondaryButtonLabel}</UppercaseBody>
        </Button>
      </ButtonGroup>
    );
  }

  return (
    <>
      {!hidePrimaryButton && (
        <Button
          variant="primary"
          data-component="modal-button-action"
          onClick={run(props.onAction, () =>
            tracking?.button.primary({ text: actionPrimaryButtonLabel.toUpperCase(), ...trackingProps })
          )}
          isDisabled={isActionPrimaryButtonDisabled}
          isFullWidth
        >
          <UppercaseBody>{actionPrimaryButtonLabel}</UppercaseBody>
        </Button>
      )}
      {!hideSecondaryButton && (
        <Button
          data-component="modal-secondary-button-action"
          variant="secondary"
          onClick={run(props.onSecondaryAction, () =>
            tracking?.button.secondary({ text: actionSecondaryButtonLabel.toUpperCase(), ...trackingProps })
          )}
          isFullWidth
        >
          <UppercaseBody>{actionSecondaryButtonLabel}</UppercaseBody>
        </Button>
      )}
    </>
  );
}

export interface ActionModalProps {
  children: React.ReactNode;
  title: string;
  actionPrimaryButtonLabel: string;
  isActionPrimaryButtonDisabled?: boolean;
  actionSecondaryButtonLabel?: string;
  size?: ModalSize;
  isVisible: boolean;
  onHide: () => void;
  onAction?: () => void;
  onSecondaryAction?: () => void;
  hideSecondaryButton?: boolean;
  hidePrimaryButton?: boolean;
  trackingProps?: TrackingProps;
}

function ActionModal(props: ActionModalProps) {
  const { tracking } = useAnalytics();
  const size = props.size || 'md';
  const trackingEvent = props.title || 'Modal';
  const actionSecondaryButtonLabel = props.actionSecondaryButtonLabel || 'cancel';
  const onSecondaryAction = props.onSecondaryAction || props.onHide;
  const trackingProps = props.trackingProps || {};

  return (
    <Modal
      role="dialog"
      isVisible={props.isVisible}
      size={size}
      onHide={run<void>(props.onHide, () => tracking?.button.icon({ text: 'Close', ...trackingProps }))}
      isHeaderFixed={true}
    >
      <Modal.Header title={props.title} size="xs" />
      <Modal.Content>
        <Content>{props.children}</Content>
      </Modal.Content>
      {props.onAction && (
        <Modal.Footer>
          <FooterButtonGroup
            trackingEvent={trackingEvent}
            actionSecondaryButtonLabel={actionSecondaryButtonLabel}
            isActionPrimaryButtonDisabled={props.isActionPrimaryButtonDisabled}
            actionPrimaryButtonLabel={props.actionPrimaryButtonLabel}
            onSecondaryAction={onSecondaryAction}
            onAction={props.onAction}
            hideSecondaryButton={props.hideSecondaryButton}
            hidePrimaryButton={props.hidePrimaryButton}
            trackingProps={trackingProps}
          />
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default ActionModal;
